"use client";

import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Checkbox,
  Grid,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  type SelectChangeEvent,
  Alert,
  TablePagination,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import { KeyboardArrowDown, ArrowForward, CheckCircle, Cancel, Warning, DoNotDisturb } from "@mui/icons-material";
import { MigrationDistributionChart } from "../MigrationDistributionChart";
import { ApiDetailsModal, getComplexityBGC, getComplexityColor, getComplexityLabel } from "./ApiDetailsModal";
// Importamos los hooks necesarios
import { useAccountId } from "../../../../hooks/useAccountId";
import type { ApiMigration } from "../../../../types/migrations";
import { useExcludeGenerate } from "../../../../hooks/useExcludeGenerate";

// Define la interfaz para los elementos de la lista de APIs
interface ApiItem {
  id: number;
  name: string;
  complexity: string;
  candidate: string;
  excluded: boolean;
  selected: boolean;
  originalMigration?: ApiMigration; // Referencia al objeto original para uso futuro
}

// Chart data
const chartData = {
  labels: ["Low Complexity", "Medium Complexity", "High Complexity"],
  values: [0, 0, 0],
  colors: ["skyblue", "#fff8e1", "#e8c8e5"],
};

interface AssessmentStepProps {
  migrationId: string;
  resListAssesmnet: any | [];
  setMigrationResult: any;
  setDisableContinue: any;
}

export function AssessmentStep({
  migrationId,
  resListAssesmnet,
  setMigrationResult,
  setDisableContinue,
}: AssessmentStepProps) {
  // Usamos el hook useAccountId directamente en el componente
  const accountId = useAccountId();
  // Estados para la UI
  const [apiList, setApiList] = useState<ApiItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedApi, setSelectedApi] = useState("");
  const [sourceSystem, setSourceSystem] = useState("IBM API Connect");
  const [destinationSystem, setDestinationSystem] = useState("Apigee");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  let [apiListAssestment, setApiListAssestment] = useState(
    Array.isArray(resListAssesmnet?.content?.assessmentResult) ? [...resListAssesmnet.content.assessmentResult] : []
  );
  const [showAlert, setShowAlert] = useState(false);
  const [msgAlert, setMsgAlert] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const { generateExcludeApi } = useExcludeGenerate();
  // const apiListAssestment = resListAssesmnet.content.assessmentResult;

  useEffect(() => {
    const newData = apiListAssestment.map((row) => ({ ...row, selected: true }));
    apiListAssestment = newData;
    setApiListAssestment(newData);
    setDisableContinue(false);
    handleSelectAll();
  }, []);

  // Mostrar alert de exlude/unexclude Api por 3 segs
  useEffect(() => {
    if (showAlert) {
      const timer = setTimeout(() => {
        setShowAlert(false);
      }, 3000);

      return () => clearTimeout(timer); // limpieza
    }
  }, [showAlert]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /**
   * Funciton Select All api list step Assessment
   */
  const handleSelectAll = () => {
    const newData = apiListAssestment.map((row) => ({ ...row, selected: true, excluded: false }));
    setApiListAssestment(newData);
    setDisableContinue(false);
  };

  /**
   * Funciton unSelect all api list step Assessment
   */
  const handleUnselectAll = () => {
    // setSelectAll(false);
    setDisableContinue(true);
    setApiListAssestment(apiListAssestment.map((api) => ({ ...api, selected: false, excluded: false })));
  };

  /**
   * Function para seleccionar o deseleccionar algun api en especifico
   * @param id Se pasa todo el row
   */
  const handleToggleSelect = (id: any) => {
    const newData = apiListAssestment.map((api) =>
      api.appId === id.appId ? { ...api, selected: !api.selected } : api
    );
    setApiListAssestment(newData);
    const countsApiSelected = newData.filter((api) => api.selected).length;
    if (countsApiSelected === 0) {
      setDisableContinue(true);
    } else {
      setDisableContinue(false);
    }
  };

  /**
   * Funciton exclude api to migrate
   * @param apiToUpdate
   */
  const handleToggleExclude = async (apiToUpdate: any) => {
    setIsLoading(true);
    setShowAlert(true);
    const resExclude = await generateExcludeApi(migrationId, accountId, apiToUpdate.appId);
    if (resExclude.content === true && resExclude.status === 200) {
      if (apiToUpdate.excluded === true) {
        handleExcludeParams(apiToUpdate, "UN-EXCLUDE");
      } else if (apiToUpdate.selected === true && apiToUpdate.excluded === false) {
        handleExcludeParams(apiToUpdate, "EXCLUDE", true);
      } else if (apiToUpdate.selected === false && apiToUpdate.excluded === true) {
        handleExcludeParams(apiToUpdate, "EXCLUDE");
      } else if (apiToUpdate.selected === false && apiToUpdate.excluded === false) {
        handleExcludeParams(apiToUpdate, "EXCLUDE");
      }
      setIsLoading(false);
    } else {
      setIsLoading(false);
      const errorMsg = `Api ${apiToUpdate.apiName}: error when try exclude`;
      setMsgAlert(errorMsg);
    }
  };

  const handleExcludeParams = (apiToUpdate: any, un_exclude: string, handleChek?: boolean) => {
    let newData: any;
    if (handleChek) {
      newData = apiListAssestment.map((api) =>
        api.appId === apiToUpdate.appId ? { ...api, excluded: !api.excluded, selected: false } : api
      );
    } else {
      newData = apiListAssestment.map((api) =>
        api.appId === apiToUpdate.appId ? { ...api, excluded: !api.excluded } : api
      );
    }
    const allExclude = newData.every((api: any) => api.excluded === true);
    const allUnCheck = newData.every((api: any) => api.selected === false);

    if (allExclude) {
      setDisableContinue(true);
    } else if (allUnCheck && allExclude) {
      setDisableContinue(true);
    } else if (!allUnCheck && allExclude) {
      setDisableContinue(true);
    } else if (allUnCheck) {
      setDisableContinue(true);
    } else {
      setDisableContinue(false);
    }
    setApiListAssestment(newData);
    const successDownloadMsg = `Api: ${apiToUpdate.apiName} ${un_exclude} successfully`;
    setMsgAlert(successDownloadMsg);
  };

  /**
   * Function open detail modal
   * @param apiInfo info row
   */
  const handleDetailClick = (apiInfo: any) => {
    setSelectedApi(apiInfo);
    setIsModalOpen(true);
  };

  const handleSourceSystemChange = (event: SelectChangeEvent) => {
    setSourceSystem(event.target.value);
  };

  const handleDestinationSystemChange = (event: SelectChangeEvent) => {
    setDestinationSystem(event.target.value);
  };

  // Count APIs by complexity
  const lowComplexity = apiListAssestment?.filter((api: any) => api.complexity === 1).length;
  const mediumComplexity = apiListAssestment?.filter((api: any) => api.complexity === 2).length;
  const highComplexity = apiListAssestment?.filter((api: any) => api.complexity === 3).length;
  const totalApis = apiListAssestment?.length;
  const reviewRequired = apiList.filter((api) => api.candidate === "No").length;

  chartData.values = [lowComplexity, mediumComplexity, highComplexity];

  resListAssesmnet.content.assessmentResult = apiListAssestment;
  setMigrationResult(resListAssesmnet);

  // Si no hay accountId, muestra un mensaje de error
  if (!accountId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">No se pudo obtener el ID de la cuenta. Por favor, inicie sesión nuevamente.</Alert>
      </Box>
    );
  }

  const handleSort = (key: any) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    const sortedData = [...apiListAssestment].sort((a, b) => {
      if (typeof a[key] === "string") {
        return direction === "asc" ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]);
      }
      return direction === "asc" ? a[key] - b[key] : b[key] - a[key];
    });

    setApiListAssestment(sortedData);
    setSortConfig({ key, direction });
  };

  const currentRows = apiListAssestment.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (apiListAssestment) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {/* General Information */}
        <Paper variant="outlined">
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6">General Information</Typography>
            {/* <IconButton size="small">
              <Edit fontSize="small" />
            </IconButton> */}
          </Box>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={3} alignItems="center">
              {/* Source System - 5/12 del ancho */}
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="source-system-label">Source System*</InputLabel>
                  <Select
                    labelId="source-system-label"
                    id="source-system"
                    value={sourceSystem}
                    label="Source System*"
                    onChange={handleSourceSystemChange}
                    IconComponent={KeyboardArrowDown}
                  >
                    <MenuItem value="IBM API Connect">IBM API Connect</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Flecha en el medio - 2/12 del ancho */}
              <Grid item xs={12} md={2} sx={{ display: "flex", justifyContent: "center" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 40,
                    width: 40,
                    bgcolor: "#f5f5f5",
                    borderRadius: "50%",
                  }}
                >
                  <ArrowForward sx={{ color: "text.secondary" }} />
                </Box>
              </Grid>

              {/* Destination System - 5/12 del ancho */}
              <Grid item xs={12} md={5}>
                <FormControl fullWidth size="small">
                  <InputLabel id="destination-system-label">Destination System*</InputLabel>
                  <Select
                    labelId="destination-system-label"
                    id="destination-system"
                    value={destinationSystem}
                    label="Destination System*"
                    onChange={handleDestinationSystemChange}
                    IconComponent={KeyboardArrowDown}
                  >
                    <MenuItem value="Apigee">Apigee X</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper variant="outlined">
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Assessment Summary
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  APIs Assessment
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 3, height: "125px" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Total APIs
                        </Typography>
                        <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {totalApis}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 3, height: "125px" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Low Complexity
                        </Typography>
                        <Chip
                          label="Low"
                          size="small"
                          sx={{
                            bgcolor: getComplexityBGC(1),
                            color: getComplexityColor(1),
                            fontSize: "0.75rem",
                            height: "20px",
                          }}
                        />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {lowComplexity}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 3, height: "125px" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Medium Complexity
                        </Typography>
                        <Chip
                          label="Mid"
                          size="small"
                          sx={{
                            bgcolor: getComplexityBGC(2),
                            color: getComplexityColor(1),
                            fontSize: "0.75rem",
                            height: "20px",
                          }}
                        />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {mediumComplexity}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper variant="outlined" sx={{ p: 3, height: "125px" }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          High Complexity
                        </Typography>
                        <Chip
                          label="High"
                          size="small"
                          sx={{
                            bgcolor: getComplexityBGC(3),
                            color: getComplexityColor(1),
                            fontSize: "0.75rem",
                            height: "20px",
                          }}
                        />
                      </Box>
                      <Typography variant="h5" fontWeight="bold">
                        {highComplexity}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12} md={5}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  Migration Distribution
                </Typography>
                <MigrationDistributionChart data={chartData} />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* API List */}
        <Paper variant="outlined">
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Typography variant="h6">API List</Typography>
            {/* <IconButton size="small">
              <Edit fontSize="small" />
            </IconButton> */}
          </Box>
          <Box sx={{ p: 2, display: "flex", justifyContent: "end", gap: 1, alignItems: "rigth" }}>
            {/* <Button variant="outlined" size="small">
              Refresh
            </Button> */}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" size="small" onClick={handleSelectAll}>
                Select all
              </Button>
              <Button variant="outlined" size="small" onClick={handleUnselectAll}>
                Unselect all
              </Button>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        fontWeight="medium"
                        onClick={() => handleSort("apiName")}
                        style={{ cursor: "pointer" }}
                      >
                        API Name
                      </Typography>
                      <KeyboardArrowDown
                        fontSize="small"
                        sx={{ ml: 0.5 }}
                        onClick={() => handleSort("apiName")}
                        style={{ cursor: "pointer" }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      Complexity
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      Candidate to migrate
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      Exclude
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium">
                      Detail
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="medium" sx={{ visibility: "hidden" }}>
                      Select
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentRows.map((api: any) => (
                  <TableRow key={api.id} sx={{ bgcolor: api.selected ? "#e3f2fd" : "inherit" }}>
                    <TableCell>
                      <Typography variant="body2">{api.apiName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getComplexityLabel(api.complexity)}
                        size="small"
                        sx={{
                          bgcolor: getComplexityBGC(api.complexity),
                          color: getComplexityColor(api.complexity),
                          fontSize: "0.75rem",
                          height: "20px",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        {api.candidate === "Yes" && (
                          <>
                            <CheckCircle sx={{ color: "#4caf50", fontSize: 16, mr: 1 }} />
                            <Typography variant="body2">Yes</Typography>
                          </>
                        )}
                        {api.candidate === "No" && (
                          <>
                            <Warning sx={{ color: "#ff9800", fontSize: 16, mr: 1 }} />
                            <Typography variant="body2">No</Typography>
                          </>
                        )}
                        {api.candidate === "Excluded" && (
                          <>
                            <Cancel sx={{ color: "#f44336", fontSize: 16, mr: 1 }} />
                            <Typography variant="body2">Excluded</Typography>
                          </>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Switch checked={api.excluded} onChange={() => handleToggleExclude(api)} size="small" />
                    </TableCell>
                    <TableCell align="center">
                      <Button variant="text" color="primary" size="small" onClick={() => handleDetailClick(api)}>
                        View
                      </Button>
                    </TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={api.selected || false}
                        disabled={api.candidate !== "Yes" || api.excluded}
                        onChange={() => handleToggleSelect(api)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {/* Mensaje de alerta de exclude */}
            <Box>
              {showAlert && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {msgAlert}
                </Alert>
              )}
            </Box>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={apiListAssestment.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Paper>

        {/* API Details Modal */}
        <ApiDetailsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          api={selectedApi}
          migrationId={migrationId}
          // xAccountId={accountId}
        />

        {isLoading && (
          <Box sx={{ p: 3, display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading}>
              <CircularProgress color="inherit" />
            </Backdrop>
          </Box>
        )}
      </Box>
    );
  }
}
